/**
 * Hook to track task processing stage via polling.
 *
 * This provides progress tracking by polling the backend for persisted
 * progress state. Progress survives page refreshes since it's stored in
 * the database.
 */
import { useState, useEffect, useCallback, useRef } from 'react';

const STAGES = {
  // Product phases (new)
  DISCOVERY: 'discovery',           // Generating discovery questions
  IDEA_SPEC: 'idea_spec',            // Generating idea specification
  // Engineering phases (existing)
  ANALYZING_REPO: 'analyzing_repo',  // GitHub repo analysis
  ANALYZING: 'analyzing',
  DECOMPOSING: 'decomposing',
  EVENT_MODELING: 'event_modeling',
  FINALIZING: 'finalizing',
};

// Map backend processing_stage to frontend stage
const BACKEND_STAGE_MAP = {
  'idle': null, // Processing complete or not started
  'discovery': STAGES.DISCOVERY,
  'idea_spec': STAGES.IDEA_SPEC,
  'analyzing_repo': STAGES.ANALYZING_REPO,
  'analyzing': STAGES.ANALYZING,
  'decomposing': STAGES.DECOMPOSING,
  'event_modeling': STAGES.EVENT_MODELING,
  'finalizing': STAGES.FINALIZING,
};

const POLL_INTERVAL = 2000; // 2 seconds

export function useProcessingStage(taskId, initialTask, initialTaskTree, isReprocessing = false) {
  // Core state from polling
  const [stage, setStage] = useState(STAGES.DISCOVERY);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progressMessages, setProgressMessages] = useState([]);
  const [progressPercent, setProgressPercent] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');

  // Derived state (computed from progress data and initial task)
  const [childTaskCount, setChildTaskCount] = useState(0);
  const [hasFeatures, setHasFeatures] = useState(false);
  const [eventModelingStarted, setEventModelingStarted] = useState(false);
  const [eventModelingCompleted, setEventModelingCompleted] = useState(false);
  const [decompositionCompleted, setDecompositionCompleted] = useState(false);

  // Idea phase tracking
  const [discoveryStarted, setDiscoveryStarted] = useState(false);
  const [discoveryQuestionsReady, setDiscoveryQuestionsReady] = useState(false);
  const [ideaSpecStarted, setIdeaSpecStarted] = useState(false);
  const [ideaSpecCompleted, setIdeaSpecCompleted] = useState(false);
  const [currentPhase, setCurrentPhase] = useState('idea');

  // GitHub repo analysis tracking
  const [repoAnalysisStarted, setRepoAnalysisStarted] = useState(false);
  const [repoAnalysisCompleted, setRepoAnalysisCompleted] = useState(false);
  const [repoAnalysisProgress, setRepoAnalysisProgress] = useState(null);

  // Track polling state
  const pollingRef = useRef(null);
  const skipInitRef = useRef(false);

  // Reset state when reprocessing starts
  useEffect(() => {
    if (isReprocessing) {
      setStage(STAGES.DISCOVERY);
      setIsProcessing(true);
      setProgressMessages([]);
      setProgressPercent(0);
      setProgressMessage('');
      setChildTaskCount(0);
      setHasFeatures(false);
      setEventModelingStarted(false);
      setEventModelingCompleted(false);
      setDecompositionCompleted(false);
      setRepoAnalysisStarted(false);
      setRepoAnalysisCompleted(false);
      setRepoAnalysisProgress(null);
      setDiscoveryStarted(false);
      setDiscoveryQuestionsReady(false);
      setIdeaSpecStarted(false);
      setIdeaSpecCompleted(false);
      setCurrentPhase('idea');
      skipInitRef.current = true;
    }
  }, [isReprocessing]);

  // Clear skip flag when plan completes
  useEffect(() => {
    const planStatus = initialTask?.plan_status || initialTask?.status;
    if (skipInitRef.current && planStatus === 'completed') {
      skipInitRef.current = false;
    }
  }, [initialTask?.plan_status, initialTask?.status]);

  // Initialize from existing data if available (skip during reprocessing)
  useEffect(() => {
    if (isReprocessing || skipInitRef.current) {
      return;
    }

    // Initialize phase state
    const taskPhase = initialTask?.current_phase;
    if (taskPhase) {
      setCurrentPhase(taskPhase);
    }

    // Check discovery state
    if (initialTask?.discovery_questions?.length > 0) {
      setDiscoveryStarted(true);
      setDiscoveryQuestionsReady(true);
    }

    // Check product spec state
    if (initialTask?.idea_spec) {
      setDiscoveryStarted(true);
      setDiscoveryQuestionsReady(true);
      setIdeaSpecStarted(true);
      setIdeaSpecCompleted(true);
    }

    // Initialize repo analysis state from context
    const repoPending = initialTask?.context?.repository_analysis_pending;
    if (repoPending || initialTask?.context?.repository) {
      setRepoAnalysisStarted(true);
      if (repoPending?.status === 'completed' || initialTask?.context?.repository_context) {
        setRepoAnalysisCompleted(true);
      }
    }

    // Check event model state
    if (initialTask?.metadata?.commands?.length > 0 ||
        initialTask?.metadata?.extracted_events?.length > 0) {
      setEventModelingCompleted(true);
    }

    // Check if stories have been generated (features present; use stories presence)
    const allStories = initialTask?.context?.user_stories || initialTask?.metadata?.user_stories || [];
    if (allStories.length > 0) {
      setHasFeatures(true);
    }

    // Check decomposition state
    if (initialTaskTree?.total_tasks > 0 ||
        initialTaskTree?.children?.length > 0 ||
        initialTask?.children_count > 0) {
      setDecompositionCompleted(true);
      setChildTaskCount(initialTaskTree?.total_tasks || initialTask?.children_count || 0);
    }

    // Determine if processing is active based on plan status
    const planStatus = initialTask?.plan_status || initialTask?.status;
    setIsProcessing(planStatus === 'processing' || planStatus === 'submitted');
  }, [initialTask, initialTaskTree, isReprocessing]);

  // Fetch progress from backend
  const fetchProgress = useCallback(async () => {
    if (!taskId) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await fetch(`/api/plans/${taskId}/progress`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        return;
      }

      const data = await response.json();

      // Update processing state
      setIsProcessing(data.is_processing);
      setProgressMessages(data.progress_messages || []);
      setProgressPercent(data.progress_percent || 0);
      setCurrentPhase(data.current_phase || 'idea');

      // Get latest message for progressMessage
      const messages = data.progress_messages || [];
      if (messages.length > 0) {
        setProgressMessage(messages[messages.length - 1].message || '');
      }

      // Map backend processing_stage to frontend stage
      const backendStage = data.processing_stage;
      const mappedStage = BACKEND_STAGE_MAP[backendStage];

      if (mappedStage) {
        setStage(mappedStage);

        // Update phase-specific state based on stage
        if (backendStage === 'discovery') {
          setDiscoveryStarted(true);
        } else if (backendStage === 'idea_spec') {
          setDiscoveryStarted(true);
          setDiscoveryQuestionsReady(true);
          setIdeaSpecStarted(true);
        } else if (backendStage === 'analyzing_repo') {
          setRepoAnalysisStarted(true);
        } else if (backendStage === 'decomposing') {
          setDiscoveryStarted(true);
          setDiscoveryQuestionsReady(true);
          setIdeaSpecStarted(true);
          setIdeaSpecCompleted(true);
        } else if (backendStage === 'event_modeling') {
          setDiscoveryStarted(true);
          setDiscoveryQuestionsReady(true);
          setIdeaSpecStarted(true);
          setIdeaSpecCompleted(true);
          setDecompositionCompleted(true);
          setEventModelingStarted(true);
        } else if (backendStage === 'finalizing') {
          setDiscoveryStarted(true);
          setDiscoveryQuestionsReady(true);
          setIdeaSpecStarted(true);
          setIdeaSpecCompleted(true);
          setDecompositionCompleted(true);
          setEventModelingStarted(true);
          setEventModelingCompleted(true);
        }
      } else if (backendStage === 'idle' && !data.is_processing) {
        // Processing complete - infer final state from phase
        if (data.current_phase === 'completed' || data.current_phase === 'engineering') {
          setDiscoveryStarted(true);
          setDiscoveryQuestionsReady(true);
          setIdeaSpecStarted(true);
          setIdeaSpecCompleted(true);
          setDecompositionCompleted(true);
          setEventModelingStarted(true);
          setEventModelingCompleted(true);
          setStage(STAGES.FINALIZING);
        } else if (data.current_phase === 'idea_spec') {
          setDiscoveryStarted(true);
          setDiscoveryQuestionsReady(true);
          setIdeaSpecStarted(true);
          setIdeaSpecCompleted(true);
          setStage(STAGES.IDEA_SPEC);
        } else if (data.current_phase === 'discovery') {
          setDiscoveryStarted(true);
          setDiscoveryQuestionsReady(true);
          setStage(STAGES.DISCOVERY);
        }
      }

      // Extract story counts from messages if available
      const decompositionMsg = messages.find(m =>
        m.message?.includes('user stories')
      );
      if (decompositionMsg) {
        const match = decompositionMsg.message.match(/(\d+)\s+user stories/);
        if (match) {
          setChildTaskCount(parseInt(match[1], 10));
        }
      }

      // Check for stories/features in messages
      const storiesMsg = messages.find(m =>
        m.message?.includes('stories') || m.message?.includes('features')
      );
      if (storiesMsg) {
        setHasFeatures(true);
      }

    } catch (error) {
      console.error('[useProcessingStage] Error fetching progress:', error);
    }
  }, [taskId]);

  // Poll for progress while processing
  useEffect(() => {
    if (!taskId) return;

    // Initial fetch
    fetchProgress();

    // Set up polling if processing or if we just started (don't know if processing yet)
    const planStatus = initialTask?.plan_status || initialTask?.status;
    const shouldPoll = isProcessing || planStatus === 'processing' || planStatus === 'submitted' || isReprocessing;

    if (shouldPoll) {
      pollingRef.current = setInterval(fetchProgress, POLL_INTERVAL);
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [taskId, isProcessing, isReprocessing, fetchProgress, initialTask?.plan_status, initialTask?.status]);

  // Stop polling when processing completes
  useEffect(() => {
    if (!isProcessing && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, [isProcessing]);

  return {
    stage,
    childTaskCount,
    hasFeatures,
    eventModelingStarted,
    eventModelingCompleted,
    decompositionCompleted,
    // Product phases
    discoveryStarted,
    discoveryQuestionsReady,
    ideaSpecStarted,
    ideaSpecCompleted,
    currentPhase,
    progressMessage,
    progressPercent,
    // GitHub repo analysis
    repoAnalysisStarted,
    repoAnalysisCompleted,
    repoAnalysisProgress,
    // New: progress messages for activity feed
    progressMessages,
    isProcessing,
  };
}

export default useProcessingStage;
